import { LoadStatus } from "../../shared/loadStatus";
import { mondayOf } from "../../shared/planDates";
import { weekHasPlan } from "../helpers/activitySelectors";
import {
  WorkoutAnalysisType,
  WorkoutDay,
  WorkoutDetails,
  WorkoutWeek,
} from "../model/workoutPlanTypes";

/**
 * State for the workout plan view. Weeks (overview + services) are cached per
 * Monday so stepping back and forth never refetches, and workout details per
 * activity and date (exercise alternatives are date-specific). `empty` is a
 * 404: no plan for that week / no detail for that workout.
 *
 * `planAvailability` is the tab's gate. There is no backend flag for a
 * workout plan, so the first load of the current week decides once: a week
 * with at least one planned entry means the member has a plan and the view
 * stays on, however empty the weeks around it are; no planned entry (or a
 * 404) sends the tab to the report-based summary.
 */

export type PlanAvailability = "unknown" | "available" | "none";

export type DetailsSelection = {
  kind: "workout" | "event" | "service";
  id: string;
  /** yyyy-MM-dd of the day the entry was opened from. */
  date: string;
};

/** A recorded session whose post-workout analysis is open in the details pane. */
export type AnalysisSelection = {
  eventId: string;
  title: string;
};

export type WorkoutPlanState = {
  /** Monday (yyyy-MM-dd) of the week shown. */
  weekStartDate: string;
  /** Monday of the week that decides `planAvailability`. */
  probeMonday: string;
  planAvailability: PlanAvailability;
  weeklyCache: Record<string, WorkoutWeek>;
  weeklyStatus: Record<string, LoadStatus>;
  details: DetailsSelection | null;
  detailsCache: Record<string, WorkoutDetails>;
  detailsStatus: Record<string, LoadStatus>;
  analysis: AnalysisSelection | null;
  analysisCache: Record<string, WorkoutAnalysisType>;
  analysisStatus: Record<string, LoadStatus>;
};

export type WorkoutPlanAction =
  | { type: "GO_TO_WEEK"; monday: string }
  | { type: "WEEKLY_LOADING"; monday: string }
  | { type: "WEEKLY_LOADED"; monday: string; week: WorkoutWeek | null }
  | { type: "WEEKLY_FAILED"; monday: string }
  | { type: "OPEN_DETAILS"; selection: DetailsSelection }
  | { type: "CLOSE_DETAILS" }
  | { type: "DETAILS_LOADING"; key: string }
  | { type: "DETAILS_LOADED"; key: string; details: WorkoutDetails | null }
  | { type: "DETAILS_FAILED"; key: string }
  | { type: "DETAILS_CLEARED" }
  | { type: "OPEN_ANALYSIS"; selection: AnalysisSelection }
  | { type: "CLOSE_ANALYSIS" }
  | { type: "ANALYSIS_LOADING"; eventId: string }
  | { type: "ANALYSIS_LOADED"; eventId: string; analysis: WorkoutAnalysisType | null }
  | { type: "ANALYSIS_FAILED"; eventId: string };

export const detailsKey = (id: string, date: string): string => `${id}|${date}`;

export const createInitialState = (today: string): WorkoutPlanState => ({
  weekStartDate: mondayOf(today),
  probeMonday: mondayOf(today),
  planAvailability: "unknown",
  weeklyCache: {},
  weeklyStatus: {},
  details: null,
  detailsCache: {},
  detailsStatus: {},
  analysis: null,
  analysisCache: {},
  analysisStatus: {},
});

/** The day for `date` from its cached week, if that week has loaded. */
export const selectDay = (
  state: WorkoutPlanState,
  date: string
): WorkoutDay | undefined =>
  state.weeklyCache[mondayOf(date)]?.daily?.find((day) => day.day === date);

const withStatus = (
  statuses: Record<string, LoadStatus>,
  key: string,
  status: LoadStatus
): Record<string, LoadStatus> => ({ ...statuses, [key]: status });

export const workoutPlanReducer = (
  state: WorkoutPlanState,
  action: WorkoutPlanAction
): WorkoutPlanState => {
  switch (action.type) {
    case "GO_TO_WEEK":
      return { ...state, weekStartDate: action.monday };

    case "WEEKLY_LOADING":
      return {
        ...state,
        weeklyStatus: withStatus(state.weeklyStatus, action.monday, "loading"),
      };

    case "WEEKLY_LOADED": {
      const isProbe =
        action.monday === state.probeMonday &&
        state.planAvailability === "unknown";
      const planAvailability: PlanAvailability = isProbe
        ? weekHasPlan(action.week)
          ? "available"
          : "none"
        : state.planAvailability;
      if (!action.week) {
        return {
          ...state,
          planAvailability,
          weeklyStatus: withStatus(state.weeklyStatus, action.monday, "empty"),
        };
      }
      return {
        ...state,
        planAvailability,
        weeklyCache: { ...state.weeklyCache, [action.monday]: action.week },
        weeklyStatus: withStatus(state.weeklyStatus, action.monday, "ready"),
      };
    }

    case "WEEKLY_FAILED":
      return {
        ...state,
        weeklyStatus: withStatus(state.weeklyStatus, action.monday, "error"),
      };

    // Picking another entry also leaves any open analysis.
    case "OPEN_DETAILS":
      return { ...state, details: action.selection, analysis: null };

    case "CLOSE_DETAILS":
      return { ...state, details: null, analysis: null };

    case "OPEN_ANALYSIS":
      return { ...state, analysis: action.selection };

    case "CLOSE_ANALYSIS":
      return { ...state, analysis: null };

    case "ANALYSIS_LOADING":
      return {
        ...state,
        analysisStatus: withStatus(state.analysisStatus, action.eventId, "loading"),
      };

    case "ANALYSIS_LOADED":
      if (!action.analysis) {
        return {
          ...state,
          analysisStatus: withStatus(state.analysisStatus, action.eventId, "empty"),
        };
      }
      return {
        ...state,
        analysisCache: { ...state.analysisCache, [action.eventId]: action.analysis },
        analysisStatus: withStatus(state.analysisStatus, action.eventId, "ready"),
      };

    case "ANALYSIS_FAILED":
      return {
        ...state,
        analysisStatus: withStatus(state.analysisStatus, action.eventId, "error"),
      };

    case "DETAILS_LOADING":
      return {
        ...state,
        detailsStatus: withStatus(state.detailsStatus, action.key, "loading"),
      };

    case "DETAILS_LOADED":
      if (!action.details) {
        return {
          ...state,
          detailsStatus: withStatus(state.detailsStatus, action.key, "empty"),
        };
      }
      return {
        ...state,
        detailsCache: { ...state.detailsCache, [action.key]: action.details },
        detailsStatus: withStatus(state.detailsStatus, action.key, "ready"),
      };

    case "DETAILS_FAILED":
      return {
        ...state,
        detailsStatus: withStatus(state.detailsStatus, action.key, "error"),
      };

    case "DETAILS_CLEARED":
      return { ...state, detailsCache: {}, detailsStatus: {}, analysisCache: {}, analysisStatus: {} };

    default:
      return state;
  }
};
